/**
 * Takes the bytes a redline never writes out of a flat OPC `pkg:package`.
 *
 * Word hands a range's OOXML as one package that holds every part of the document
 * the range sits in. A document that embeds its fonts ("Embed fonts in the file")
 * adds each font as a base64 `pkg:binaryData` part under `/word/fonts/`, so one
 * paragraph's read measured 8.3 to 9.0 million characters in a contract that embeds
 * ten fonts, against 75 thousand without them. Every merge entry the bulk apply builds copies
 * those parts, so each entry outgrows the batch's payload cap alone and the run
 * writes its cards one at a time.
 *
 * WHY the fonts can go from every package
 *   The document keeps its own embedded fonts whatever a write carries: six writes
 *   of font-stripped packages into a document that embeds ten fonts left its font
 *   parts, its embed flag and its file size unchanged (Word for Mac).
 *   The copy in a package is a by-product of the read, so nothing is stored and
 *   nothing is put back.
 */

/**
 * The text every embedded font part's opening tag holds, so a package without one is
 * returned untouched after one scan.
 */
const EMBEDDED_FONT_PART_MARKER = 'pkg:name="/word/fonts/';

/**
 * One embedded font part: its opening tag, named under `/word/fonts/`, through its
 * closing tag. The base64 inside holds no `<`, so the first `</pkg:part>` after the
 * opening tag is this part's own.
 *
 * EXAMPLE
 *   <pkg:part pkg:name="/word/fonts/font3.odttf" pkg:contentType="application/vnd.openxmlformats-officedocument.obfuscatedFont" pkg:compression="store"><pkg:binaryData>AAEA…</pkg:binaryData></pkg:part>
 *   -> matched whole, 1.6 million characters for the largest of ten fonts
 */
const EMBEDDED_FONT_PART = /<pkg:part\b[^>]*\bpkg:name="\/word\/fonts\/[^"]*"[^>]*>[\s\S]*?<\/pkg:part>/g;

/**
 * An embedded font's reference in `fontTable.xml`: the element that names the font
 * part by relationship id, in each of the four faces Word embeds.
 *
 * EXAMPLE
 *   <w:embedRegular r:id="rId1" w:fontKey="{C5C9095D-717A-9542-96FC-043A21093B7B}"/> -> matched
 */
const EMBEDDED_FONT_REFERENCE = /<w:embed(?:Regular|Bold|Italic|BoldItalic)\b[^>]*\/>/g;

/** The relationships part of `fontTable.xml`, the part that lists each embedded font part as a relationship. */
const FONT_TABLE_RELATIONSHIPS_PART = /<pkg:part\b[^>]*\bpkg:name="\/word\/_rels\/fontTable\.xml\.rels"[^>]*>[\s\S]*?<\/pkg:part>/g;

/** One relationship of the font type, the shape of a relationship to an embedded font part. */
const FONT_RELATIONSHIP = /<Relationship\b[^>]*\bType="[^"]*\/relationships\/font"[^>]*\/>/g;

/** The start of any relationship element, to tell a relationships part with relationships left from an empty one. */
const ANY_RELATIONSHIP_START = /<Relationship\b/;

/**
 * The character that stands where a page's body content goes in a package taken apart
 * for its pages. XML never holds it, so it names one place in the package.
 */
export const PAGE_BODY_CONTENT_MARKER = "\u0000";

/**
 * One media part: an image or an embedded object, its opening tag through its closing
 * tag, with its part name captured. The base64 inside holds no `<`.
 *
 * EXAMPLE
 *   <pkg:part pkg:name="/word/media/image1.png" pkg:contentType="image/png" pkg:compression="store"><pkg:binaryData>iVBOR…</pkg:binaryData></pkg:part>
 *   -> matched whole, with "/word/media/image1.png" captured
 */
const MEDIA_PART = /<pkg:part\b[^>]*\bpkg:name="(\/word\/(?:media|embeddings)\/[^"]*)"[^>]*>[\s\S]*?<\/pkg:part>/g;

/** One relationships part, its opening tag through its closing tag, with its part name captured. */
const RELATIONSHIPS_PART = /<pkg:part\b[^>]*\bpkg:name="(\/[^"]*\.rels)"[^>]*>[\s\S]*?<\/pkg:part>/g;

/** The relationships part of the document body, whose relationships the body's own elements point at. */
const DOCUMENT_RELATIONSHIPS_PART_NAME = "/word/_rels/document.xml.rels";

/** One relationship element, in the self-closing form Word writes. */
const RELATIONSHIP_ELEMENT = /<Relationship\b[^>]*\/>/g;

/** A relationship's id, as its attribute spells it. */
const RELATIONSHIP_ID = /\bId="([^"]*)"/;

/** A relationship's target, as its attribute spells it. */
const RELATIONSHIP_TARGET = /\bTarget="([^"]*)"/;

/** The mark of a relationship that points outside the package, which names no part of it. */
const EXTERNAL_RELATIONSHIP = /\bTargetMode="External"/;

/** Each quoted attribute value in a run of XML, whatever the attribute: where a body names a relationship by its id. */
const QUOTED_ATTRIBUTE_VALUE = /="([^"]*)"/g;

/** One relationship from the document body to a media part, as the page's cut needs it. */
interface DocumentMediaRelationship {
    relationshipId: string;
    mediaPartName: string;
}

/**
 * The part a relationship's target names, as the package names it.
 *
 * CASES
 *   - the target starts with "/"                -> that name, with its "." and ".." segments resolved
 *   - otherwise                                 -> the target taken from the folder the relationships part describes, resolved the same way
 *
 * WHY the "." and ".." segments are resolved
 *   A part in a subfolder reaches a sibling folder by climbing out of its own: a chart names its
 *   workbook "../embeddings/Microsoft_Excel_Sheet1.xlsx", a diagram's drawing names its image
 *   "../media/image9.png". Joined as written, the name "/word/charts/../embeddings/…" is no part's
 *   name, so the relationship read as pointing at no media part and its part was left out.
 *
 * EXAMPLE
 *   "/word/_rels/document.xml.rels", "media/image1.png"                          -> "/word/media/image1.png"
 *   "/_rels/.rels", "docProps/thumbnail.jpeg"                                    -> "/docProps/thumbnail.jpeg"
 *   "/word/charts/_rels/chart1.xml.rels", "../embeddings/Microsoft_Excel_Sheet1.xlsx" -> "/word/embeddings/Microsoft_Excel_Sheet1.xlsx"
 */
function partNameOfRelationshipTarget(relationshipsPartName: string, relationshipTarget: string): string {
    const folderOfDescribedPart = relationshipsPartName.replace(/_rels\/[^/]*$/, "");
    const targetFromRoot = relationshipTarget.startsWith("/") ? relationshipTarget : `${folderOfDescribedPart}${relationshipTarget}`;
    const resolvedSegments: string[] = [];
    for (const segment of targetFromRoot.split("/")) {
        /**
         * CASES
         *   - an empty segment or "." -> stays where it is: nothing to add
         *   - ".."                    -> the folder before it is left
         *   - any other segment       -> a folder or the part's file name
         */
        if (segment === "" || segment === ".") continue;
        if (segment === "..") resolvedSegments.pop();
        else resolvedSegments.push(segment);
    }
    return `/${resolvedSegments.join("/")}`;
}

export class PackageBinaryParts {
    /**
     * The package without its embedded fonts: each font part, each reference to one in
     * `fontTable.xml`, and the font relationships that listed them.
     *
     * WHY the references and relationships go with the parts
     *   A reference to a part the package no longer holds would dangle, and Word reads
     *   a dangling relationship as a damaged package.
     *
     * CASES
     *   - the package holds no embedded font part  -> returned as given, the same string
     *   - it holds some                            -> without the parts, the references and the font relationships
     *   - the fontTable relationships part lists only fonts -> that part goes too
     *   - it lists anything else as well                    -> that part stays, with the other relationships
     *
     * EXAMPLE
     *   10 font parts of 1.6 million characters each, 8,958,297 characters in all
     *   -> 75,771 characters: the document, the styles and the parts around them
     *
     * RETURNS
     *   a package that parses as well as the one given, and that this method returns
     *   unchanged when given again.
     */
    static withoutEmbeddedFonts(packageOoxml: string): string {
        const holdsEmbeddedFontPart = packageOoxml.includes(EMBEDDED_FONT_PART_MARKER);
        /**
         * WHEN the package holds no embedded font part
         *   CONSEQUENCE
         *     the same string comes back after the one scan, so a document without
         *     embedded fonts pays nothing here.
         */
        if (!holdsEmbeddedFontPart) return packageOoxml;
        const withoutFontParts = packageOoxml.replace(EMBEDDED_FONT_PART, "");
        const withoutFontReferences = withoutFontParts.replace(EMBEDDED_FONT_REFERENCE, "");
        return withoutFontReferences.replace(FONT_TABLE_RELATIONSHIPS_PART, (fontTableRelationshipsPartXml) => {
            const remainingPartXml = fontTableRelationshipsPartXml.replace(FONT_RELATIONSHIP, "");
            /**
             * CASES
             *   - a relationship other than a font's is left -> the part stays with it
             *   - none is left                                -> the part goes
             */
            return ANY_RELATIONSHIP_START.test(remainingPartXml) ? remainingPartXml : "";
        });
    }
}

/**
 * A whole-document package taken apart so each page of it carries only the media that
 * page uses.
 *
 * A page is a few of the document's paragraphs set in the package around the body. That
 * package lists every image and embedded object of the whole document, so a page of two
 * paragraphs carried the bytes of every image in a contract with images. A merge entry
 * built over the page carried them out again, and one image of 1.7 million characters
 * put each entry over half the batch cap.
 *
 * WHY a page can leave media out
 *   The document holds its own media. A page needs the media its paragraphs name by
 *   relationship id, so the paragraph it writes keeps its picture, and the media that
 *   something outside the body uses (a header, a footer, the document's background).
 *   Every other media part, and its relationship, is not a part of what the write says.
 */
export class PackageMediaForPages {
    private constructor(
        private readonly packageWithoutMedia: string,
        private readonly mediaPartXmlByPartName: Map<string, string>,
        private readonly documentMediaRelationships: DocumentMediaRelationship[],
        private readonly mediaPartNamesUsedOutsideTheDocumentBody: Set<string>,
        private readonly attributeValuesOutsideTheBodyAndTheRelationships: Set<string>,
    ) {}

    /**
     * Takes the media out of a package that holds a marker where its body content goes,
     * keeping what it needs to put the right media back for each page.
     *
     * CASES, for each relationship in the package
     *   - from the document body to a media part                   -> remembered by id, for the page that names it
     *   - from any other part (a header's, a footer's) to a media part -> that media part is used outside the body: every page keeps it
     *   - to anything else, or outside the package                 -> left alone
     *
     * EXAMPLE
     *   document.xml.rels  rId5 -> media/image1.png, rId6 -> media/image2.png
     *   header1.xml.rels   rId1 -> media/image3.png
     *   -> image1 and image2 by id for the pages that name them; image3 kept by every page
     */
    static separatedFrom(packageWithBodyContentMarker: string): PackageMediaForPages {
        const mediaPartXmlByPartName = new Map<string, string>();
        const packageWithoutMedia = packageWithBodyContentMarker.replace(MEDIA_PART, (mediaPartXml, mediaPartName: string) => {
            mediaPartXmlByPartName.set(mediaPartName, mediaPartXml);
            return "";
        });
        const documentMediaRelationships: DocumentMediaRelationship[] = [];
        const mediaPartNamesUsedOutsideTheDocumentBody = new Set<string>();
        for (const relationshipsPartMatch of packageWithoutMedia.matchAll(RELATIONSHIPS_PART)) {
            const relationshipsPartName = relationshipsPartMatch[1];
            for (const relationshipMatch of relationshipsPartMatch[0].matchAll(RELATIONSHIP_ELEMENT)) {
                const relationshipXml = relationshipMatch[0];
                /** WHEN the relationship points outside the package CONSEQUENCE it names no part of it, so no media part. */
                if (EXTERNAL_RELATIONSHIP.test(relationshipXml)) continue;
                const relationshipTarget = RELATIONSHIP_TARGET.exec(relationshipXml)?.[1];
                const relationshipId = RELATIONSHIP_ID.exec(relationshipXml)?.[1];
                /** WHEN the relationship names no target or no id CONSEQUENCE there is nothing to match a media part to. */
                if (relationshipTarget === undefined || relationshipId === undefined) continue;
                const targetPartName = partNameOfRelationshipTarget(relationshipsPartName, relationshipTarget);
                /** WHEN the relationship's target is not a media part CONSEQUENCE it is not this class's to place. */
                if (!mediaPartXmlByPartName.has(targetPartName)) continue;
                /**
                 * CASES
                 *   - the body's own relationships part -> remembered by id, for the page that names it
                 *   - any other part's                  -> used outside the body
                 */
                if (relationshipsPartName === DOCUMENT_RELATIONSHIPS_PART_NAME) documentMediaRelationships.push({ relationshipId, mediaPartName: targetPartName });
                else mediaPartNamesUsedOutsideTheDocumentBody.add(targetPartName);
            }
        }
        const packageWithoutRelationships = packageWithoutMedia.replace(RELATIONSHIPS_PART, "");
        const attributeValuesOutsideTheBodyAndTheRelationships = new Set(Array.from(packageWithoutRelationships.matchAll(QUOTED_ATTRIBUTE_VALUE), (valueMatch) => valueMatch[1]));
        return new PackageMediaForPages(packageWithoutMedia, mediaPartXmlByPartName, documentMediaRelationships, mediaPartNamesUsedOutsideTheDocumentBody, attributeValuesOutsideTheBodyAndTheRelationships);
    }

    /**
     * The package with this page's body content in it, and only the media the page uses.
     *
     * WHY the attribute values outside the body count as use
     *   The document's own background names its picture from the package around the body,
     *   not from a paragraph, so a page that left it out would lose the picture. Every value
     *   the package holds outside its relationships parts is read as a possible use, and a
     *   media part kept for a coincidence costs only its own bytes.
     *
     * CASES, for each media relationship of the document body
     *   - its id is named in the page's body, or elsewhere outside the relationships -> the relationship and its media part stay
     *   - its id is named nowhere                                                    -> the relationship goes, and its media part
     *                                                                                  unless a header or a footer uses it
     *   - the package holds no media                                                 -> the package as it is, with the page's body in it
     *
     * EXAMPLE
     *   image1 named by the page's paragraph (rId5), image2 by no paragraph of it (rId6), image3 by a header
     *   -> the page's package holds image1 and image3, the rId5 relationship, and not rId6 or image2
     */
    packageForPageBodyContent(pageBodyContent: string): string {
        const holdsMedia = this.mediaPartXmlByPartName.size > 0;
        /**
         * WHEN the package holds no media
         *   CONSEQUENCE
         *     only the page's body goes in. The replacement is a function, so a `$` in the page's
         *     words ("$1.00") is never read as a replacement pattern.
         */
        if (!holdsMedia) return this.packageWithoutMedia.replace(PAGE_BODY_CONTENT_MARKER, () => pageBodyContent);
        const attributeValuesOfThePage = new Set(Array.from(pageBodyContent.matchAll(QUOTED_ATTRIBUTE_VALUE), (valueMatch) => valueMatch[1]));
        const relationshipIdsOfUnusedMedia = new Set<string>();
        const mediaPartNamesToKeep = new Set(this.mediaPartNamesUsedOutsideTheDocumentBody);
        for (const { relationshipId, mediaPartName } of this.documentMediaRelationships) {
            const isUsed = attributeValuesOfThePage.has(relationshipId) || this.attributeValuesOutsideTheBodyAndTheRelationships.has(relationshipId);
            /**
             * CASES
             *   - the relationship's id is named by the page or by the package around it -> its media part stays
             *   - it is named nowhere                                                    -> its relationship goes
             */
            if (isUsed) mediaPartNamesToKeep.add(mediaPartName);
            else relationshipIdsOfUnusedMedia.add(relationshipId);
        }
        const withoutRelationshipsOfUnusedMedia = this.packageWithoutMedia.replace(RELATIONSHIPS_PART, (relationshipsPartXml, relationshipsPartName: string) => {
            /** WHEN this is not the body's own relationships part CONSEQUENCE its relationships stay as they are. */
            if (relationshipsPartName !== DOCUMENT_RELATIONSHIPS_PART_NAME) return relationshipsPartXml;
            return relationshipsPartXml.replace(RELATIONSHIP_ELEMENT, (relationshipXml) => {
                const relationshipId = RELATIONSHIP_ID.exec(relationshipXml)?.[1];
                /**
                 * CASES
                 *   - the relationship is of a media part no page text names -> it goes
                 *   - any other relationship                                  -> it stays
                 */
                return relationshipId !== undefined && relationshipIdsOfUnusedMedia.has(relationshipId) ? "" : relationshipXml;
            });
        });
        const keptMediaPartsXml = Array.from(mediaPartNamesToKeep, (mediaPartName) => this.mediaPartXmlByPartName.get(mediaPartName) ?? "").join("");
        const packageEnd = withoutRelationshipsOfUnusedMedia.lastIndexOf("</pkg:package>");
        /**
         * CASES
         *   - the package closes with `</pkg:package>` -> the kept media parts go in before it
         *   - it holds no closing tag                  -> the kept media parts follow it
         *
         * EXAMPLE
         *   "...</pkg:part></pkg:package>" with the closing tag at index 40 -> slice(0, 40) + media + slice(40)
         */
        const withKeptMedia = packageEnd < 0
            ? `${withoutRelationshipsOfUnusedMedia}${keptMediaPartsXml}`
            : `${withoutRelationshipsOfUnusedMedia.slice(0, packageEnd)}${keptMediaPartsXml}${withoutRelationshipsOfUnusedMedia.slice(packageEnd)}`;
        return withKeptMedia.replace(PAGE_BODY_CONTENT_MARKER, () => pageBodyContent);
    }
}
